import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Box,
  Button,
  Alert,
} from '@mui/material';
import { AddBox, ArrowBack, ArrowForward } from '@mui/icons-material';
import { useBundleCreation } from '../../contexts/BundleCreationContext';
import PageHeader from '../common/PageHeader';
import PageContainer from '../common/PageContainer';
import BundleSelector from './BundleSelector';
import QuantityInput from './QuantityInput';
import StockValidator from './StockValidator';
import ChildSKUPicker from './ChildSKUPicker';
import BundleCreator from './BundleCreator';

const BundleCreationWorkflow: React.FC = () => {
  const { state, dispatch, loadDraft, canProceedToNextStep, getActiveWorkflowSteps } = useBundleCreation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const workflowSteps = getActiveWorkflowSteps();

  useEffect(() => {
    const draftId = searchParams.get('resume');
    if (draftId && draftId !== state.draftId) {
      loadDraft(draftId).catch(err => {
        console.error('Failed to load draft from URL:', err);
      });
    }
  }, [searchParams, loadDraft, state.draftId]);

  const handleNext = () => {
    if (canProceedToNextStep()) {
      dispatch({ type: 'NEXT_STEP' });
    }
  };

  const handleBack = () => {
    dispatch({ type: 'PREVIOUS_STEP' });
  };

  const handleReset = () => {
    dispatch({ type: 'RESET_WORKFLOW' });
  };

  const handleComplete = () => {
    dispatch({ type: 'RESET_WORKFLOW' });
    setSearchParams({}); // Clear query parameters
    navigate('/'); // Redirect to dashboard
  };

  const renderStepContent = () => {
    switch (state.currentStep) {
      case 0:
        return <BundleSelector />;
      case 1:
        return <QuantityInput />;
      case 2:
        return <StockValidator />;
      case 3:
        return <ChildSKUPicker />;
      case 4:
        return <BundleCreator onComplete={handleComplete} />;
      default:
        return <Typography>Unknown step</Typography>;
    }
  };

  return (
    <PageContainer>
      {/* Header Section */}
      <PageHeader
        icon={<AddBox />}
        title="Add bundle Inventory"
        subtitle="Follow the guided workflow to create a new bundle SKU"
      />

      {/* Progress Stepper */}
      <Card
        sx={{
          mb: 4,
          background: '#ffffff',
          border: '1px solid',
          borderColor: '#ECECE4',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Stepper
            activeStep={state.currentStep}
            alternativeLabel
            sx={{
              '& .MuiStepLabel-root': {
                '& .MuiStepLabel-iconContainer': {
                  '& .MuiStepIcon-root': {
                    fontSize: { xs: '1.1rem', sm: '1.6rem', md: '2rem' },
                    '&.Mui-active': {
                      color: 'primary.main',
                    },
                    '&.Mui-completed': {
                      color: 'success.main',
                    },
                  },
                },
                '& .MuiStepLabel-labelContainer': {
                  '& .MuiStepLabel-label': {
                    fontWeight: 600,
                    fontSize: { xs: '0.65rem', sm: '0.8rem', md: '0.9rem' },
                    '&.Mui-active': {
                      color: 'primary.main',
                    },
                    '&.Mui-completed': {
                      color: 'success.main',
                    },
                  },
                },
              },
            }}
          >
            {workflowSteps.map((step) => (
              <Step key={step.id} completed={step.completed}>
                <StepLabel>{step.name}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step Content */}
      <Card
        sx={{
          background: '#ffffff',
          border: '1px solid',
          borderColor: '#ECECE4',
          boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.05), 0px 2px 4px -1px rgba(0, 0, 0, 0.03)',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 }, minHeight: { xs: 'auto', md: 450 } }}>
          <Box className="fade-in">
            {renderStepContent()}
          </Box>
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 0 } }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={state.currentStep === 0 || state.unicommerceSyncCompleted}
          startIcon={<ArrowBack />}
          sx={{
            py: 1.25,
            px: { xs: 2, sm: 3 },
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '999px',
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          Previous
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleReset}
            disabled={state.unicommerceSyncCompleted}
            sx={{
              py: 1.25,
              px: { xs: 2, sm: 3 },
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '999px',
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Reset
          </Button>

          {state.currentStep < workflowSteps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceedToNextStep()}
              endIcon={<ArrowForward />}
              sx={{
                py: 1.25,
                px: { xs: 2, sm: 3 },
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '999px',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0px 4px 12px rgba(220, 88, 41, 0.3)',
                },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>

      {/* Validation Alerts */}
      {state.validationResult && !state.validationResult.isValid && (
        <Alert
          severity="warning"
          sx={{
            mt: 3,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'warning.light',
            '& .MuiAlert-icon': {
              fontSize: '1.5rem',
            },
          }}
        >
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Stock Validation Issues:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {state.validationResult.errors.map((error, index) => (
              <Typography key={index} variant="body2" component="li" sx={{ mb: 0.5 }}>
                {error}
              </Typography>
            ))}
          </Box>
        </Alert>
      )}
    </PageContainer>
  );
};

export default BundleCreationWorkflow;